import React, { useRef, useEffect, useState } from 'react';

export default function Creator() {
  const ref = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 968);

  useEffect(() => {
    // Handle responsive state
    const handleResize = () => setIsMobile(window.innerWidth < 968);
    window.addEventListener('resize', handleResize);

    // Animation observer
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) e.target.classList.add('feat-visible'); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  return (
    <section id="creator" ref={ref} style={{
      ...styles.section, 
      padding: isMobile ? '60px 20px' : '100px 20px'
    }}>
      <div style={{
        ...styles.container,
        flexDirection: isMobile ? 'column' : 'row',
        textAlign: isMobile ? 'center' : 'left',
        gap: isMobile ? '40px' : '50px'
      }}>
        {/* Left Side: Text Content */}
        <div style={{
          ...styles.content,
          minWidth: isMobile ? '100%' : '320px'
        }}>
          <span style={styles.badge}>Creator Program</span>
          <h2 style={styles.title}>
            Turn your expertise into <span style={styles.highlight}>Guided Tours.</span>
          </h2>
          <p style={styles.text}>
            Join Shepherd as a creator building interactive guides for the world's top platforms. 
            Help others navigate complex software while building your own brand.
          </p>
          
          <ul style={{
            ...styles.list,
            display: 'inline-block', // Keeps bullet points aligned on mobile
            textAlign: 'left'
          }}>
            <li style={styles.listItem}><span style={styles.bullet}>•</span> Build guides in seconds with Shepherd's easy interface</li>
            <li style={styles.listItem}><span style={styles.bullet}>•</span> Let others follow your guides in real-time</li>
            <li style={styles.listItem}><span style={styles.bullet}>•</span> See if your guide becomes a top guide</li>
          </ul>

          <div style={{ marginTop: '20px' }}>
            <button style={{
              ...styles.button,
              width: isMobile ? '100%' : 'auto'
            }}>Get Started for Free</button>
          </div>
        </div>

        {/* Right Side: Visual/Card */}
        <div style={{
          ...styles.cardContainer,
          minWidth: isMobile ? '100%' : '320px'
        }}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.dot}></div>
              <div style={styles.dot}></div>
              <div style={styles.dot}></div>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.skeletonLine}></div>
              <div style={{...styles.skeletonLine, width: '60%'}}></div>
              <div style={{
                ...styles.creatorAction,
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                <div style={styles.avatar}>LM</div>
                <div style={{ textAlign: 'left' }}>
                  <div style={styles.creatorName}>LM17</div>
                  <div style={styles.creatorSub}>Verified Shepherd Creator</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const styles = {
  section: {
    width: '100%',
    backgroundColor: '#ffffff',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  container: {
    maxWidth: '1100px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  content: {
    flex: '1',
  },
  badge: {
    backgroundColor: 'rgba(26, 115, 232, 0.1)',
    color: '#1a73e8',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    display: 'inline-block',
    marginBottom: '20px',
  },
  title: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    color: '#121212',
    lineHeight: '1.1',
    fontWeight: '800',
    marginBottom: '24px',
  },
  highlight: { color: '#1a73e8' },
  text: {
    fontSize: '1.1rem',
    color: '#555',
    lineHeight: '1.6',
    marginBottom: '30px',
    maxWidth: '500px',
  },
  list: { listStyle: 'none', padding: 0, marginBottom: '40px' },
  listItem: {
    fontSize: '1rem',
    color: '#333',
    marginBottom: '12px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center'
  },
  bullet: { color: '#1a73e8', fontWeight: 'bold', marginRight: '10px', fontSize: '1.2rem' },
  button: {
    backgroundColor: '#1a73e8',
    color: 'white',
    padding: '16px 32px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  cardContainer: { flex: '1', display: 'flex', justifyContent: 'center' },
  card: {
    width: '100%',
    maxWidth: '380px',
    backgroundColor: '#f8f9fa',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    border: '1px solid #eee',
  },
  cardHeader: { padding: '12px', backgroundColor: '#eee', display: 'flex', gap: '6px' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ccc' },
  cardBody: { padding: '30px' },
  skeletonLine: { height: '12px', backgroundColor: '#ddd', borderRadius: '6px', marginBottom: '15px' },
  creatorAction: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '30px' },
  avatar: { 
    width: '40px', height: '40px', borderRadius: '50%', 
    backgroundColor: '#1a73e8', color: 'white', display: 'flex', 
    alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' 
  },
  creatorName: { fontWeight: '700', fontSize: '0.9rem' },
  creatorSub: { fontSize: '0.75rem', color: '#888' },
};